import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ModalBodyStyles, ModalContentStyles, ModalFooterActionsStyles, ModalFooterStyles, ModalHeaderStyles, ModalHeadingStyles, ModalStyles } from './styles/Modal.styles';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    size?: 'small' | 'default' | 'large';
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
    className?: string;
    overlayClassName?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
}

interface ModalHeaderProps {
    children: ReactNode;
    className?: string;
}

interface ModalBodyProps {
    children: ReactNode;
    className?: string;
}

interface ModalFooterProps {
    children: ReactNode;
    className?: string;
}

// Modal component that renders within the current DOM context (shadow DOM)
export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    className = '',
    overlayClassName = '',
    ariaLabel,
    ariaLabelledBy
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

    // Create portal container within the current DOM context
    useEffect(() => {
        if (isOpen) {
            // Store the currently focused element
            previousActiveElement.current = document.activeElement as HTMLElement;

            // Create a container for the modal within the current DOM
            const container = document.createElement('div');
            container.setAttribute('data-modal-portal', 'true');
            container.style.position = 'fixed';
            container.style.top = '0';
            container.style.left = '0';
            container.style.width = '100%';
            container.style.height = '100%';
            container.style.zIndex = '9999';
            container.style.display = 'flex';

            // Append to the current document context (works within shadow DOM)
            const shadowRoot = document.getElementById("twilio-webchat-widget-root")?.shadowRoot || document.body;
            shadowRoot.appendChild(container);
            setPortalContainer(container);

            // Focus trap
            const focusableElements = modalRef.current?.querySelectorAll(
                'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
            );

            if (focusableElements && focusableElements.length > 0) {
                (focusableElements[0] as HTMLElement).focus();
            }

            return () => {
                // Restore focus to previous element
                if (previousActiveElement.current && previousActiveElement.current.focus) {
                    previousActiveElement.current.focus();
                }

                // Clean up portal container
                if (container && container.parentNode) {
                    container.parentNode.removeChild(container);
                }
            };
        }
    }, [isOpen]);

    // Handle escape key press
    useEffect(() => {
        if (!isOpen || !closeOnEscape) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, closeOnEscape]);

    // Handle focus trap
    useEffect(() => {
        if (!isOpen || !modalRef.current) return;

        const handleTabKey = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            const focusableElements = modalRef.current!.querySelectorAll(
                'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
            );

            const firstElement = focusableElements[0] as HTMLElement;
            const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, [isOpen]);

    if (!isOpen || !portalContainer) return null;

    const modalContent = (
        <div
            className={overlayClassName}
            style={ModalStyles}
            onClick={(e) => {
                if (closeOnOverlayClick && e.target === e.currentTarget) {
                    onClose();
                }
            }}
            role="presentation"
        >
            <div
                ref={modalRef}
                className={className}
                style={ModalContentStyles}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );

    return createPortal(modalContent, portalContainer);
};

// Modal Header Component
export const ModalHeader: React.FC<ModalHeaderProps> = ({ children, className = '' }) => {
    return (
        <div style={ModalHeaderStyles} className={className}>
            {children}
        </div>
    );
};

// Modal Body Component
export const ModalBody: React.FC<ModalBodyProps> = ({ children, className = '' }) => {
    return (
        <div className={className} style={ModalBodyStyles}>
            {children}
        </div>
    );
};

// Modal Footer Component
export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => {
    return (
        <div style={ModalFooterStyles} className={className}>
            {children}
        </div>
    );
};

// Modal Heading Component (for use within ModalHeader)
export const ModalHeading: React.FC<{ children: ReactNode; id?: string; className?: string }> = ({
    children,
    id,
    className = ''
}) => {
    return (
        <h3 id={id} className={className} style={ModalHeadingStyles}>
            {children}
        </h3>
    );
};

// Modal Footer Actions Component (for use within ModalFooter)
export const ModalFooterActions: React.FC<{ children: ReactNode; className?: string }> = ({
    children,
    className = ''
}) => {
    return (
        <div className={className} style={ModalFooterActionsStyles}>
            {children}
        </div>
    );
};